import { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import gql from "graphql-tag";
import { isStaffBrowser } from "utils/staff";
import { ComponentMap } from "../type-name-mappings";
import {
  CONTROL_VARIANT_KEY,
  resolveVariant,
  VariantContainerEntry,
} from "./resolveVariant";
import { useVariantInTime } from "./useVariantInTime";
import { VariantSwitcher } from "./VariantSwitcher";
import styles from "./VariantContainer.module.css";

// QA/design override, mirroring the localStorage opt-in on the Lassie+ flag:
// `localStorage.setItem("dev:ab:<flagKey>", "treatment")` pins a variant, so a
// preview can be reviewed without an Amplitude rollout bucket. In staff mode
// (`?lassie_staff=1`) the container shows a switcher that sets it.
const storageKey = (flagKey: string) => `dev:ab:${flagKey}`;

const readOverride = (flagKey: string): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(storageKey(flagKey));
};

// Read after mount only: the statically generated HTML has no access to
// localStorage, so reading it during render would desync hydration.
const useVariantOverride = (flagKey: string) => {
  const [override, setOverride] = useState<string | null>(null);

  useEffect(() => {
    setOverride(readOverride(flagKey));
  }, [flagKey]);

  const pinVariant = (variantKey: string) => {
    window.localStorage.setItem(storageKey(flagKey), variantKey);
    setOverride(variantKey);
  };

  return { override, pinVariant };
};

// Staff mode is a cookie, so it is read after mount for the same reason.
const useIsStaff = () => {
  const [staff, setStaff] = useState(false);

  useEffect(() => {
    setStaff(isStaffBrowser());
  }, []);

  return staff;
};

/**
 * Renders one component out of an Amplitude Experiment variant container. Any
 * component a page body can hold can be a variant.
 *
 * The page is statically generated with every variant in the payload, so the
 * server-rendered markup is the control entry and the Amplitude Experiment SDK
 * picks the real variant in the browser. To avoid a visible flip, the wrapper
 * stays unpainted until the SDK has answered (see the CSS module), for at most
 * the hide timer. A visitor whose answer comes later stays on control and is
 * left out of the experiment (`useVariantInTime`).
 */
export const ContentfulVariantContainer: FC<VariantContainerEntry> = (
  container
) => {
  const flagKey = container.experimentId ?? "";
  const wrapper = useRef<HTMLDivElement>(null);
  const { variant, answered } = useVariantInTime(flagKey, wrapper);
  const { override, pinVariant } = useVariantOverride(flagKey);
  const staff = useIsStaff();

  const variantKey = override ?? variant;
  const entry = resolveVariant(container, variantKey);

  if (!entry) {
    console.warn(
      `Variant container ${flagKey} has no linked variants, rendering nothing`
    );
    return null;
  }

  // `type-name-mappings` imports this component, so the import below is
  // circular. Reading the map only here — at render, never at module scope —
  // keeps it safe: by the time a page renders, both modules are initialised.
  const Component = ComponentMap?.[entry.__typename ?? ""];

  if (!Component) {
    console.warn(
      `Variant container ${flagKey} resolved to unknown component ${entry.__typename}`
    );
    return null;
  }

  const variantKeys = Object.keys(container.meta ?? {});

  return (
    <div
      ref={wrapper}
      className={classNames(!answered && styles.pending, staff && styles.staff)}
      data-ab-flag={flagKey}
      data-ab-variant={variantKey ?? ""}
    >
      <Component {...entry} />
      {staff && variantKeys.length > 1 && (
        <VariantSwitcher
          flagKey={flagKey}
          variantKeys={variantKeys}
          current={variantKey ?? CONTROL_VARIANT_KEY}
          onPick={pinVariant}
        />
      )}
    </div>
  );
};

// Not attached as `ContentfulVariantContainer.fragment`: fragments exposed that
// way are auto-collected into every page query, and this one can only be sent
// once the `variantContainer` content type exists in the stitched Contentful
// schema. `src/contentful/queries` spreads it behind an env flag instead.
//
// The variants spread the same fragments as a page body. Contentful prices a
// query by its worst case, so every variant slot costs about 2,200 against
// the 11,000 cap: 3 slots put the page query near 6,600, 5 near 10,900, and
// 10 fail every page with TOO_COMPLEX_QUERY (measured October 2026).
// Variants an editor links past the limit are dropped and render as control.
export const variantContainerFragment = gql`
  fragment VariantContainer on VariantContainer {
    __typename
    experimentId
    meta
    variantsCollection(limit: 3) {
      items {
        __typename
        ...Hero
        ...Benefits
        ...Component
        ...Cards
        ...TextAndList
        ...TextAndMedia
        ...TextAndText
        ...Packages
        ...FAQ
        ...RichText
        ...StructuredData
        ...TextAndMediaCarousel
        ...PromoBanner
      }
    }
    sys {
      id
      publishedAt
    }
  }
`;
